import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Container,
	Grid,
	Typography,
	Button,
	CircularProgress,
	IconButton,
} from "@material-ui/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LanguageIcon from "@mui/icons-material/Language";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import BlockIcon from "@mui/icons-material/Block";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PetsIcon from "@mui/icons-material/Pets";
import AccessibleIcon from "@mui/icons-material/Accessible";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CivitatisExperienceRepository from "../../../repositories/civitatisExperienceRepository";
import { getSessionStorage } from "../../../utils/getSessionStorage";
import { setPaxCount } from "../../../utils/civitatisPax";
import {
	sessionAvailability,
	maxSessionQuota,
	dayAvailabilityError,
	bookableSessions,
} from "../../../utils/civitatisAvailability";
import { useGlobal } from "../../../contexts/globalContext";
import RegisterModal from "../../RegisterModal";
import decodeHtmlEntities, { htmlToText } from "../../../utils/decodeHtmlEntities";

const useStyles = makeStyles((theme) => ({
	root: { backgroundColor: "#f8f9fb", minHeight: "100vh", paddingBottom: 60 },
	topBar: { background: "#fff", borderBottom: "1px solid #eee", padding: "10px 0", marginBottom: 0 },
	breadcrumb: { fontSize: 12, color: "#7f7f7f", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, "&:hover": { textDecoration: "underline" } },

	// Header
	titleBlock: { padding: "20px 0 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, [theme.breakpoints.down("xs")]: { flexDirection: "column", gap: 12 } },
	titleLeft: { flex: 1, minWidth: 0 },
	title: { fontSize: 26, fontWeight: 700, color: "#1a1a2e", marginBottom: 6, lineHeight: 1.3 },
	ratingRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
	ratingScore: { display: "flex", alignItems: "center", gap: 4, fontSize: 14, fontWeight: 700, color: theme.palette.primary.main },
	ratingCount: { fontSize: 13, color: "#888" },
	badge: { display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600 },
	badgeGreen: { color: "#2e7d32" },
	badgeGray: { color: "#666" },
	// Title right: info chips + price — all in one row
	titleRight: { display: "flex", flexDirection: "row", alignItems: "center", gap: 20, flexShrink: 0 },
	infoChipRow: { display: "flex", gap: 20 },
	infoChip: { display: "flex", flexDirection: "column", alignItems: "center", gap: 5, minWidth: 56 },
	infoChipIcon: { color: theme.palette.primary.main, fontSize: "24px !important" },
	infoChipLabel: { fontSize: 11, color: "#888", textAlign: "center", lineHeight: 1.3, maxWidth: 80 },
	chipDivider: { width: 1, height: 40, background: "#e0e0e0", flexShrink: 0, margin: "0 4px" },
	headerPriceFrom: { fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: 1, textAlign: "center", margin: 0, marginBottom: 2 },
	headerPriceValue: { fontSize: 24, fontWeight: 800, color: theme.palette.primary.main, lineHeight: 1.1, textAlign: "center", margin: 0 },

	// Gallery — full container width, taller hero
	galleryWrapper: { display: "flex", gap: 4, height: 420, marginBottom: 24, borderRadius: 14, overflow: "hidden", width: "100%" },
	galleryMain: { flex: "0 0 60%", overflow: "hidden" },
	galleryMainImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
	galleryThumbs: { flex: 1, display: "flex", flexDirection: "row", gap: 4 },
	galleryThumb: { flex: 1, overflow: "hidden" },
	galleryThumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

	// Main body
	card: { background: "#fff", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
	descriptionText: { fontSize: 14, color: "#444", lineHeight: 1.7, whiteSpace: "pre-line" },
	showMoreBtn: { color: theme.palette.primary.main, fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 6, display: "inline-block", background: "none", border: "none", padding: 0 },

	// Details grid
	detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
	detailItem: { display: "flex", gap: 10, alignItems: "flex-start" },
	detailIcon: { color: theme.palette.primary.main, fontSize: "20px !important", marginTop: 2, flexShrink: 0 },
	detailLabel: { fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 },
	detailValue: { fontSize: 13, color: "#444", lineHeight: 1.5 },

	sectionTitle: { fontSize: 16, fontWeight: 700, color: "#1a1a2e", marginBottom: 12 },

	// Sidebar
	sidebar: { background: "#fff", borderRadius: 12, padding: "20px 20px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", position: "sticky", top: 80 },

	// Sidebar price row
	sidebarPriceRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 },
	sidebarPriceLabel: { fontSize: 13, color: "#555" },
	sidebarPriceRight: { textAlign: "right" },
	sidebarPricePts: { fontSize: 20, fontWeight: 800, color: theme.palette.primary.main },
	sidebarPricePer: { fontSize: 12, color: "#888", marginLeft: 2 },
	// Passenger box
	paxBox: { border: "1px solid #e0e0e0", borderRadius: 8, padding: "12px 14px", marginBottom: 4 },
	paxBoxTitle: { fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 10 },
	paxHint: { fontSize: 11, color: "#888", marginTop: -6, marginBottom: 8 },
	paxError: { fontSize: 12, color: "#d32f2f", fontWeight: 600, marginTop: 12, textAlign: "center" },
	passengerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #f5f5f5" },
	paxName: { fontSize: 13, color: "#333", fontWeight: 500 },
	paxSub: { fontSize: 11, color: "#999" },
	qtyControls: { display: "flex", alignItems: "center", gap: 10 },
	qtyBtn: { width: 24, height: 24, borderRadius: "50%", border: "1.5px solid #ccc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, lineHeight: 1, userSelect: "none", fontWeight: 700, color: "#555", "&:hover": { borderColor: theme.palette.primary.main, color: theme.palette.primary.main } },
	qtyBtnDisabled: { opacity: 0.35, cursor: "not-allowed", "&:hover": { borderColor: "#ccc", color: "#555" } },
	qtyValue: { fontWeight: 700, minWidth: 18, textAlign: "center", fontSize: 14 },

	// Calendar
	calendarHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 10px" },
	calendarMonth: { fontSize: 14, fontWeight: 700, color: "#1a1a2e" },
	calendarGrid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, marginBottom: 8 },
	calendarWeekday: { textAlign: "center", fontSize: 11, color: "#aaa", paddingBottom: 6, fontWeight: 600 },
	calendarDay: {
		height: 34, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
		borderRadius: 8, textAlign: "center", fontSize: 13, cursor: "pointer",
		"&:hover": { background: "#f5f0ff", color: theme.palette.primary.main },
	},
	calendarDaySelected: { background: theme.palette.primary.main, color: "#fff !important", fontWeight: 700, borderRadius: 8 },
	calendarDayDisabled: { color: "#d0d0d0 !important", cursor: "default", "&:hover": { background: "transparent", color: "#d0d0d0 !important" } },
	dynamicDot: { display: "block", width: 4, height: 4, borderRadius: "50%", background: "#2e7d32", margin: "1px auto 0" },

	// CTA
	ctaBtn: { width: "100%", marginTop: 16, height: 50, borderRadius: 8, fontWeight: 700, fontSize: 15, textTransform: "none" },

	// Big session cards section
	availabilitySection: { marginTop: 8, marginBottom: 24 },
	availabilityTitle: { fontSize: 17, fontWeight: 700, color: "#1a1a2e", marginBottom: 16 },
	bigSessionCard: {
		position: "relative", border: "1px solid #e0e0e0", borderRadius: 12,
		overflow: "hidden", marginBottom: 16, display: "flex", background: "#fff",
		transition: "box-shadow 0.2s",
		"&:hover": { boxShadow: "0 4px 18px rgba(0,0,0,0.10)" },
		[theme.breakpoints.down("sm")]: { flexDirection: "column" },
	},
	bigSessionCardSelected: { border: `2px solid ${theme.palette.primary.main}` },
	bigSessionBadge: {
		position: "absolute", top: 0, left: 0,
		background: theme.palette.primary.main, color: "#fff",
		fontSize: 12, fontWeight: 700, padding: "5px 14px",
		borderBottomRightRadius: 10,
	},
	bigSessionLeft: { flex: "0 0 55%", padding: "44px 28px 24px" },
	bigSessionRight: {
		flex: 1, padding: "24px 24px 20px",
		borderLeft: "1px solid #f0f0f0",
		display: "flex", flexDirection: "column",
		[theme.breakpoints.down("sm")]: { borderLeft: "none", borderTop: "1px solid #f0f0f0" },
	},
	bigSessionTypeName: { fontSize: 18, fontWeight: 700, color: theme.palette.primary.main, marginBottom: 10 },
	bigSessionDivider: { height: 1, background: "#ebebeb", margin: "12px 0" },
	bigSessionMetaRow: { display: "flex", gap: 36 },
	bigSessionMetaLabel: { fontSize: 11, fontWeight: 700, color: "#333", marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.4 },
	bigSessionMetaValue: { fontSize: 14, color: "#555" },
	bigSessionPriceLabel: { fontSize: 11, color: "#aaa", marginBottom: 2 },
	bigSessionPriceValue: { fontSize: 22, fontWeight: 800, color: theme.palette.primary.main, marginBottom: 10 },
	bigSessionPaxLine: { fontSize: 12, color: "#666", marginBottom: 2 },
	bigSessionQuota: { fontSize: 12, fontWeight: 700, marginTop: 8, marginBottom: 4 },
	bigSessionSelectBtn: { width: "100%", marginTop: "auto", height: 46, borderRadius: 8, fontWeight: 700, fontSize: 14, textTransform: "none" },
}));

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTH_LABELS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const normalizeImageUrl = (img) => {
	if (!img) return null;
	if (typeof img === "string") return img;
	return img.url || img.src || img.href || null;
};

const getActivityImages = (detail) => {
	const p = detail?.photos;
	if (p) {
		const gallery = Array.isArray(p.gallery) ? p.gallery : (Array.isArray(p.header) ? p.header : []);
		if (gallery.length) {
			return gallery.map((item) => {
				const url = item.paths?.original || item.paths?.big || item.paths?.thumbnail;
				return url ? { url, caption: item.caption } : null;
			}).filter(Boolean);
		}
	}
	if (detail?.images?.length) {
		return detail.images.map((img) => ({ url: normalizeImageUrl(img) })).filter((i) => i.url);
	}
	const single = detail?.image || detail?.imageUrl || detail?.mainImage;
	return single ? [{ url: single }] : [];
};

// Funções removidas - agora usando decodeHtmlEntities e htmlToText do utils

// Converts an included/notIncluded field (array or HTML string) to a string array
const toItems = (value) => {
	if (!value) return [];
	if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
	const text = htmlToText(value);
	const parts = text.split("\n").map((s) => s.trim()).filter(Boolean);
	return parts.length > 1 ? parts : text ? [text] : [];
};

const LANG_NAMES = { pt: "Português", en: "Inglês", es: "Espanhol", fr: "Francês", de: "Alemão", it: "Italiano" };

// Group (private) categories: max people per service. Falls back to the "Até N pessoas" label.
const getGroupMax = (cat) =>
	cat?.group?.maximum ?? (parseInt(String(cat?.text || cat?.name || "").match(/\d+/)?.[0], 10) || Infinity);

// Civitatis: /v2/activities returns "miniAge"; keep "minimumAge" for older payloads.
const getActivityMinAge = (detail) => detail?.miniAge || detail?.minimumAge || 0;

// Calendar v2 rate category: age: { minimum, maximum }. ponytail: max >= 99 treated as "no limit".
const formatAgeRange = (age) => {
	const min = age?.minimum > 0 ? age.minimum : null;
	const max = age?.maximum > 0 && age.maximum < 99 ? age.maximum : null;
	if (min && max) return `${min} a ${max} anos`;
	if (min) return `a partir de ${min} anos`;
	if (max) return `até ${max} anos`;
	return null;
};

// Lowest minimum age among the option's categories; falls back to the activity's minimum age.
const getSessionMinAge = (categories, detail) => {
	const mins = categories.map((c) => c?.age?.minimum).filter((n) => n > 0);
	return mins.length ? Math.min(...mins) : getActivityMinAge(detail);
};

const civitatisRepo = new CivitatisExperienceRepository();

const parseCalArray = (cal) =>
	Array.isArray(cal) ? cal
	: Array.isArray(cal?.schedule) ? cal.schedule
	: Array.isArray(cal?.dates) ? cal.dates
	: [];

const CivitatisActivityDetail = () => {
	const classes = useStyles();
	const theme = useTheme();
	const history = useHistory();
	const { user } = useGlobal();
	const sessionsRef = useRef(null);
	const [isOpenRegisterModal, setIsOpenRegisterModal] = useState(false);
	const handleRegisterModal = () => setIsOpenRegisterModal((prev) => !prev);

	const stored = getSessionStorage("@EntertainmentSelected") || {};
	const { activityId, refToken, activity: storedActivity } = stored;

	const [detail, setDetail] = useState(storedActivity || null);
	const [calendarData, setCalendarData] = useState([]);
	const [loadingDetail, setLoadingDetail] = useState(true);
	const [descExpanded, setDescExpanded] = useState(false);

	const [paxCounts, setPaxCounts] = useState({ 0: 1 }); // { [categoryId]: count }, initialized with 1 adult

	const [calendarMonth, setCalendarMonth] = useState(() => {
		const d = new Date();
		return { year: d.getFullYear(), month: d.getMonth() };
	});
	const [selectedDate, setSelectedDate] = useState(null);
	const [sessions, setSessions] = useState([]);
	const [loadingSessions, setLoadingSessions] = useState(false);
	const [selectedSession, setSelectedSession] = useState(null);
	const [paxError, setPaxError] = useState(null);

	useEffect(() => {
		if (!activityId || !refToken) return;
		civitatisRepo.getActivity({ activityId, refToken }).then((data) => {
			if (data) {
				setDetail(data || storedActivity || null);
				setCalendarData(parseCalArray(data.calendar));
				const cats = data?.rates?.[0]?.categories || [];
				if (cats.length > 0) {
					const next = {};
					cats.forEach((cat, i) => { next[cat.id] = data.isCategoryPaxGroup ? 0 : (i === 0 ? 1 : 0); });
					setPaxCounts(next);
				}
			}
			setLoadingDetail(false);
		});
	}, [activityId, refToken]);

	// When the user navigates to a month not covered by current calendarData, fetch it.
	const fetchedMonths = useRef(new Set());
	useEffect(() => {
		if (!activityId || !refToken) return;
		const { year, month } = calendarMonth;
		const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
		if (fetchedMonths.current.has(monthKey)) return;

		const isCovered = calendarData.some((e) => e?.date?.startsWith(monthKey));
		if (isCovered) {
			fetchedMonths.current.add(monthKey);
			return;
		}

		fetchedMonths.current.add(monthKey);
		const dateFrom = `${year}-${String(month + 1).padStart(2, "0")}-01`;
		civitatisRepo.getActivity({ activityId, refToken, date: dateFrom }).then((data) => {
			if (data?.calendar) {
				const newEntries = parseCalArray(data.calendar);
				if (newEntries.length > 0) {
					setCalendarData((prev) => {
						const existing = new Set(prev.map((e) => e?.date));
						return [...prev, ...newEntries.filter((e) => !existing.has(e?.date))];
					});
				}
			}
		});
	}, [calendarMonth, activityId, refToken]);

	const handleDateSelect = useCallback(async (dateStr) => {
		setSelectedDate(dateStr);
		setSelectedSession(null);
		setSessions([]);
	}, []);

	const extractTimeSessionsFromCalendar = (calArray, dateStr, activityData) => {
		const dateEntry = (calArray || []).find((e) => e?.date === dateStr);
		const nested = dateEntry?.hours || dateEntry?.options || dateEntry?.sessions || dateEntry?.times || dateEntry?.modalities || [];
		const multiEntries = (calArray || []).filter((e) => e?.date === dateStr && (e?.time || e?.startTime || e?.hour));

		if (nested.length > 0) {
			return nested.map((s) => ({
				time: typeof s === "string" ? s : (s?.time || s?.startTime || s?.hour || null),
				price: (typeof s === "object" ? s?.price : null) ?? activityData?.price ?? null,
				pointsPrice: (typeof s === "object" ? s?.pointsPrice : null) ?? activityData?.pointsPrice ?? null,
				rates: activityData?.rates ?? null,
				id: activityData?.id ?? 0,
			})).filter((s) => s.time);
		}
		if (multiEntries.length > 1) {
			return multiEntries.map((e) => ({
				time: e.time || e.startTime || e.hour,
				price: e.price ?? activityData?.price ?? null,
				pointsPrice: e.pointsPrice ?? activityData?.pointsPrice ?? null,
				rates: activityData?.rates ?? null,
				id: activityData?.id ?? 0,
			}));
		}
		return [];
	};

	const handleVerifyAvailability = async () => {
		if (!selectedDate) return;

		const minPax = detail?.minimumPaxPerBooking || 0;
		const chosenPax = Object.values(paxCounts).reduce((a, b) => a + b, 0);
		if (minPax > 1 && chosenPax < minPax) {
			setPaxError(`Esta atividade requer um número mínimo de ${minPax} passageiros.`);
			return;
		}
		setPaxError(null);

		setLoadingSessions(true);
		const data = await civitatisRepo.getActivity({ activityId, refToken, date: selectedDate });

		// Backend returns details.sessions when the calendar has time slots for the date.
		// Also check the fresh calendar in the response as fallback.
		let normalized = Array.isArray(data)
			? data
			: Array.isArray(data?.sessions) ? data.sessions
			: Array.isArray(data?.options) ? data.options
			: Array.isArray(data?.modalities) ? data.modalities
			: [];

		// Defeito 2: quando a API diz que a data não tem vaga nenhuma, não se
		// remonta opção a partir do calendário nem da atividade inteira — essas
		// opções nascem sem quota e passariam por cima do bloqueio.
		const semDisponibilidade = data?.hasAvailability === false;

		if (!semDisponibilidade && normalized.length === 0) {
			// Try extracting time slots from the calendar returned in this response
			const cal = data?.calendar;
			const freshCal = Array.isArray(cal) ? cal
				: Array.isArray(cal?.schedule) ? cal.schedule
				: Array.isArray(cal?.dates) ? cal.dates
				: [];
			normalized = extractTimeSessionsFromCalendar(freshCal, selectedDate, data);
		}

		if (!semDisponibilidade && normalized.length === 0 && (data?.price != null || data?.pointsPrice != null || data?.minimumPrice != null)) {
			// Single activity with no specific time slots — treat entire activity as one session
			normalized = [data];
		}

		normalized = bookableSessions(normalized);

		// A data pode ainda ter menos vagas do que as pessoas escolhidas.
		const dayQuota = maxSessionQuota(normalized);
		const semOpcao = semDisponibilidade || normalized.length === 0;
		const dayError = semOpcao
			? "Não há disponibilidade para esta data."
			: dayAvailabilityError(dayQuota, totalPax, isGroup);
		setPaxError(dayError);
		setSessions(semOpcao ? [] : normalized);
		setLoadingSessions(false);
		if (semOpcao) return;
		if (normalized.length > 0) {
			setTimeout(() => sessionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
		}
	};

	const paxCategories = detail?.rates?.[0]?.categories || [];
	const isGroup = !!detail?.isCategoryPaxGroup;
	const totalPax = Math.max(1, Object.values(paxCounts).reduce((a, b) => a + b, 0));
	// Vagas da data já consultada: trava o seletor de pessoas no limite real.
	const dayQuota = maxSessionQuota(sessions);
	const atQuotaCap = !isGroup && dayQuota !== null && totalPax >= dayQuota;
	const adults = paxCounts[paxCategories[0]?.id ?? 0] ?? 1;

	const handleReserve = (session = selectedSession) => {
		if (!session) return;
		const sessionPointsPrice = session.pointsPrice ?? detail?.pointsPrice ?? null;
		const sessionCashPrice = session.price ?? detail?.price ?? null;
		const sessionRateCategories = session.rates?.[0]?.categories || [];
		// Group activities: paxCounts holds people per group; price is charged once per group, not per person
		const totalPoints = detail?.isCategoryPaxGroup && sessionRateCategories.length > 0
			? sessionRateCategories.reduce((sum, rc) => {
				const qty = paxCounts[rc.id] ?? 0;
				return sum + (qty > 0 ? Math.round(rc.pointsPrice ?? sessionPointsPrice ?? 0) : 0);
			}, 0) || null
			: (sessionPointsPrice ? Math.round(sessionPointsPrice * totalPax) : null);
		const totalBrl = !sessionCashPrice ? null
			: detail?.isCategoryPaxGroup && totalPoints && sessionPointsPrice
				? sessionCashPrice * (totalPoints / sessionPointsPrice)
				: sessionCashPrice * totalPax;
		sessionStorage.setItem(
			"@EntertainmentAvailabilitySelected",
			JSON.stringify({
				date: selectedDate,
				session,
				paxCounts,
				adults,
				children: totalPax - adults,
				totalPoints,
				totalBrl,
				activity: detail,
				activityId,
				refToken,
			})
		);
		if (user?.guest || user?.travelVoucher) {
			return handleRegisterModal();
		}
		history.push("/busca/entretenimento/perguntas");
	};

	// Calendar
	const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
	const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

	// Maps date → first calendar entry (for availability/hasDynamicPrice display).
	// Uses the first entry so multiple time-based entries for the same date don't overwrite each other.
	const calendarAvailMap = (Array.isArray(calendarData) ? calendarData : []).reduce((acc, entry) => {
		if (entry?.date && !acc[entry.date]) acc[entry.date] = entry;
		return acc;
	}, {});

	const renderCalendar = () => {
		const { year, month } = calendarMonth;
		const daysInMonth = getDaysInMonth(year, month);
		const firstDay = getFirstDayOfMonth(year, month);
		const today = new Date().toISOString().split("T")[0];
		const cells = [];
		for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
		for (let d = 1; d <= daysInMonth; d++) {
			const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
			const entry = calendarAvailMap[dateStr];
			const isPast = dateStr < today;
			// Uma data só está disponível se: não é passado + existe no retorno da API + não está marcada como indisponível
			const isAvailable = !isPast && entry && entry.available !== false;
			const isSelected = dateStr === selectedDate;
			cells.push(
				<div
					key={dateStr}
					className={[
						classes.calendarDay,
						isSelected ? classes.calendarDaySelected : "",
						!isAvailable ? classes.calendarDayDisabled : "",
					].join(" ")}
					onClick={() => isAvailable && handleDateSelect(dateStr)}
				>
					{d}
					{entry?.hasDynamicPrice && isAvailable && <span className={classes.dynamicDot} />}
				</div>
			);
		}
		return cells;
	};

	if (loadingDetail) {
		return (
			<div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
				<CircularProgress />
			</div>
		);
	}

	if (!detail) {
		return (
			<Container style={{ paddingTop: 40 }}>
				<Typography>Atividade não encontrada.</Typography>
				<Button onClick={() => history.goBack()}>Voltar</Button>
			</Container>
		);
	}

	const media = getActivityImages(detail);
	const activityTitle = decodeHtmlEntities(detail.title || detail.name || "Atividade");
	const minPointsPrice = selectedSession?.pointsPrice ?? detail?.pointsPrice ?? null;
	const hasFreeCancellation = Array.isArray(detail.cancelPolicies)
		? detail.cancelPolicies.some((p) => p.penalty === 0 || p.penalty === 0.0)
		: detail.daysBeforeCancellationPolicy > 0;
	const hasPickup = detail.hasPickup;
	const rating = detail.score ?? detail.rating ?? null;
	const reviewCount = detail.reviews || detail.numComments || detail.reviewCount;

	const durationMin = detail.duration?.min;
	const durationDisplay = durationMin
		? durationMin >= 60 ? `${Math.round(durationMin / 60)} hora${Math.round(durationMin / 60) !== 1 ? "s" : ""}` : `${durationMin} min`
		: null;

	const langDisplay = (detail.guideLanguages || [])
		.map((l) => LANG_NAMES[l] || l)
		.join(", ");

	// Descrição curta e longa separadas
	const descShort = decodeHtmlEntities(detail.description || "");
	const descLongRaw = decodeHtmlEntities(detail.longDescription || "");

	// Formata descrição longa: adiciona quebras após pontos finais e interrogações
	const descLong = descLongRaw
		? descLongRaw
			.replace(/\.\s+([A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞ])/g, ".\n\n$1") // Ponto + maiúscula
			.replace(/\?\s+([A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞ])/g, "?\n\n$1") // Interrogação + maiúscula
			.replace(/!\s+([A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞ])/g, "!\n\n$1") // Exclamação + maiúscula
		: "";

	const hasLongDescription = descLong && descLong.length > 0;

	const includedItems = toItems(detail.included);
	const notIncludedItems = toItems(detail.notIncluded);
	const accessibilityText = Array.isArray(detail.accessibility)
		? detail.accessibility.join(", ")
		: typeof detail.accessibility === "object" && detail.accessibility !== null
			? decodeHtmlEntities(detail.accessibility.description || "")
			: decodeHtmlEntities(detail.accessibility || "");
	const voucherText = htmlToText(detail.voucherType || detail.infoVoucher || "");
	const whenToBookMinutes = detail.advance?.minutes_before ?? null;
	const whenToBookText = whenToBookMinutes != null
		? (() => {
			const hours = Math.floor(whenToBookMinutes / 60);
			const mins = whenToBookMinutes % 60;
			if (hours > 0 && mins === 0) return `${hours} hora${hours !== 1 ? "s" : ""} de antecedência`;
			if (hours > 0) return `${hours}h${mins}min de antecedência`;
			return `${mins} minuto${mins !== 1 ? "s" : ""} de antecedência`;
		})()
		: decodeHtmlEntities(detail.whenToBook || detail.advanceBooking || detail.bookingInfo || "");

	const NOT_INFORMED = "Verifique a descrição da atividade para detalhes.";

	const detailItems = [
		{ icon: <LanguageIcon className={classes.detailIcon} />, label: "Idioma", value: langDisplay || NOT_INFORMED },
		{ icon: <CheckCircleOutlineIcon className={classes.detailIcon} />, label: "Incluído", ...(includedItems.length > 0 ? { items: includedItems } : { value: NOT_INFORMED }) },
		{ icon: <BlockIcon className={classes.detailIcon} />, label: "Não incluído", ...(notIncludedItems.length > 0 ? { items: notIncludedItems } : { value: NOT_INFORMED }) },
		{ icon: <AccessTimeIcon className={classes.detailIcon} />, label: "Duração", value: durationDisplay || NOT_INFORMED },
		{ icon: <EventAvailableIcon className={classes.detailIcon} />, label: "Quando reservar", value: whenToBookText || NOT_INFORMED },
		// OCULTO POR SOLICITAÇÃO DO PRODUTO - Para reativar, descomente a linha abaixo:
		// { icon: <ConfirmationNumberOutlinedIcon className={classes.detailIcon} />, label: "Detalhes do voucher", value: voucherText || NOT_INFORMED, preformatted: true },
		{ icon: <AccessibleIcon className={classes.detailIcon} />, label: "Acessibilidade", value: accessibilityText || NOT_INFORMED },
	];

	return (
		<>
		<div className={classes.root}>
			{/* Top breadcrumb bar */}
			<div className={classes.topBar}>
				<Container maxWidth="lg">
					<span className={classes.breadcrumb} onClick={() => history.goBack()}>
						<ArrowBackIcon style={{ fontSize: 14 }} />
						Voltar aos resultados
					</span>
				</Container>
			</div>

			<Container maxWidth="lg">
				{/* Title + rating */}
				<div className={classes.titleBlock}>
					{/* Left: title + badges */}
					<div className={classes.titleLeft}>
						<Typography className={classes.title}>{activityTitle}</Typography>
						<div className={classes.ratingRow}>
							{rating !== null && (
								<span className={classes.ratingScore}>
									<StarIcon style={{ fontSize: 16, color: "#f5a623" }} />
									{Number(rating).toFixed(1)}/10
									{reviewCount > 0 && <span className={classes.ratingCount}>{reviewCount} opiniões</span>}
								</span>
							)}
							{hasFreeCancellation && (
								<span className={`${classes.badge} ${classes.badgeGreen}`}>
									<CheckCircleOutlineIcon style={{ fontSize: 15 }} />
									Cancelamento gratuito
								</span>
							)}
							{hasPickup && (
								<span className={`${classes.badge} ${classes.badgeGray}`}>
									<DirectionsWalkIcon style={{ fontSize: 15 }} />
									Recolha no hotel
								</span>
							)}
						</div>
					</div>

					{/* Right: info chips + price — single row */}
					<div className={classes.titleRight}>
						{detail.hasDynamicPrice && (
							<div className={classes.infoChip}>
								<MonetizationOnOutlinedIcon className={classes.infoChipIcon} />
								<span className={classes.infoChipLabel}>Preço pode variar conforme a data</span>
							</div>
						)}
						{getActivityMinAge(detail) > 0 && (
							<div className={classes.infoChip}>
								<PersonOutlineIcon className={classes.infoChipIcon} />
								<span className={classes.infoChipLabel}>Idade mínima {getActivityMinAge(detail)} anos</span>
							</div>
						)}
						{minPointsPrice != null && (
							<>
								<div className={classes.chipDivider} />
								<div style={{ textAlign: "center" }}>
									<p className={classes.headerPriceFrom}>A PARTIR DE</p>
									<p className={classes.headerPriceValue}>
										{Math.round(minPointsPrice).toLocaleString("pt-BR")}
										<span style={{ fontSize: 15, fontWeight: 700 }}> Pts</span>
									</p>
								</div>
							</>
						)}
					</div>
				</div>

				{/* Full-width gallery */}
				{media.length > 0 && (
					<div className={classes.galleryWrapper}>
						<div className={classes.galleryMain}>
							<img src={media[0]?.url} alt={activityTitle} className={classes.galleryMainImg} />
						</div>
						{media.length > 1 && (
							<div className={classes.galleryThumbs}>
								{media.slice(1, 3).map((img, i) => (
									<div key={i} className={classes.galleryThumb}>
										<img src={img.url} alt={`${activityTitle} ${i + 2}`} className={classes.galleryThumbImg} />
									</div>
								))}
							</div>
						)}
					</div>
				)}

				<Grid container spacing={3}>
					{/* Left column */}
					<Grid item xs={12} md={8}>
						{/* Description */}
						<div className={classes.card}>
							{/* Descrição curta - sempre visível */}
							{descShort && (
								<Typography className={classes.descriptionText}>
									{descShort}
								</Typography>
							)}

							{/* Descrição longa - toggle */}
							{hasLongDescription && descExpanded && (
								<Typography className={classes.descriptionText} style={{ marginTop: 16 }}>
									{descLong}
								</Typography>
							)}

							{/* Botão toggle */}
							{hasLongDescription && (
								<button className={classes.showMoreBtn} onClick={() => setDescExpanded((v) => !v)}>
									{descExpanded ? "Ver resumo" : "Ver a descrição completa"}
								</button>
							)}

							{/* Details grid */}
							{detailItems.length > 0 && (
								<>
									<Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>Detalhes</Typography>
									<div className={classes.detailsGrid}>
										{detailItems.map((item, i) => (
											<div key={i} className={classes.detailItem}>
												{item.icon}
												<div>
													<p className={classes.detailLabel}>{item.label}</p>
													{item.items ? (
														<ul style={{ margin: 0, paddingLeft: 16, listStyleType: "disc" }}>
															{item.items.map((v, j) => (
																<li key={j} className={classes.detailValue} style={{ marginBottom: 2 }}>{v}</li>
															))}
														</ul>
													) : (
														<p className={classes.detailValue} style={item.preformatted ? { whiteSpace: "pre-line" } : undefined}>{item.value}</p>
													)}
												</div>
											</div>
										))}
									</div>
								</>
							)}
						</div>

						{/* Cancel policy */}
						{detail.cancelPolicy && (
							<div className={classes.card}>
								<Typography className={classes.sectionTitle}>Política de Cancelamento</Typography>
								<Typography className={classes.descriptionText}>{htmlToText(detail.cancelPolicy)}</Typography>
							</div>
						)}
					</Grid>

					{/* Right sidebar */}
					<Grid item xs={12} md={4}>
						<div className={classes.sidebar}>
							{/* Price row */}
							{minPointsPrice != null && (
								<div className={classes.sidebarPriceRow}>
									<span className={classes.sidebarPriceLabel}>A partir de :</span>
									<div className={classes.sidebarPriceRight}>
										<span className={classes.sidebarPricePts}>
											{Math.round(minPointsPrice).toLocaleString("pt-BR")} pts
										</span>
										<span className={classes.sidebarPricePer}>{detail?.isCategoryPaxGroup ? "/ por serviço" : "/ por pessoa"}</span>
									</div>
								</div>
							)}

							{/* Passenger box */}
							<div className={classes.paxBox}>
								<Typography className={classes.paxBoxTitle}>
									Escolha a quantidade de pessoas{" "}
									<span style={{ color: theme.palette.primary.main, fontWeight: 400 }}>(Obrigatório)</span>
								</Typography>
								{detail?.minimumPaxPerBooking > 1 && (
									<Typography className={classes.paxHint}>
										Mínimo de {detail.minimumPaxPerBooking} passageiros por reserva
									</Typography>
								)}
								{detail?.isCategoryPaxGroup && paxCategories.length > 1 && (
									<Typography className={classes.paxHint}>
										Selecione apenas uma faixa de grupo por reserva.
									</Typography>
								)}
								{(paxCategories.length > 0 ? paxCategories : [{ id: 0, text: "Adultos" }]).map((cat, i) => {
									const minVal = detail?.isCategoryPaxGroup ? 0 : (i === 0 ? 1 : 0);
									const maxVal = detail?.isCategoryPaxGroup ? getGroupMax(cat) : Infinity;
									const val = paxCounts[cat.id] ?? (detail?.isCategoryPaxGroup ? 0 : (i === 0 ? 1 : 0));
									const ageSub = formatAgeRange(cat.age);
									return (
										<div key={cat.id} className={classes.passengerRow}>
											<div>
												<span className={classes.paxName}>{cat.text}</span>
												{ageSub && <span className={classes.paxSub}> ({ageSub})</span>}
											</div>
											<div className={classes.qtyControls}>
												<div className={classes.qtyBtn} onClick={() => { setPaxError(null); setPaxCounts((p) => setPaxCount(p, cat.id, Math.max(minVal, (p[cat.id] ?? 0) - 1), isGroup)); }}>−</div>
												<span className={classes.qtyValue}>{val}</span>
												<div
													className={`${classes.qtyBtn} ${val >= maxVal || atQuotaCap ? classes.qtyBtnDisabled : ""}`}
													aria-disabled={val >= maxVal || atQuotaCap}
													title={atQuotaCap
														? `Restam ${dayQuota} vagas para ${selectedDate ? selectedDate.split("-").reverse().join("/") : "esta data"}`
														: val >= maxVal ? `Capacidade máxima: ${maxVal} pessoas` : undefined}
													onClick={() => { if (val >= maxVal || atQuotaCap) return; setPaxError(null); setPaxCounts((p) => setPaxCount(p, cat.id, Math.min(maxVal, (p[cat.id] ?? 0) + 1), isGroup)); }}
												>+</div>
											</div>
										</div>
									);
								})}
							</div>

							{/* Calendar */}
							<div className={classes.calendarHeader}>
								<IconButton size="small" onClick={() => setCalendarMonth(({ year, month }) => {
									const p = new Date(year, month - 1);
									return { year: p.getFullYear(), month: p.getMonth() };
								})}>
									<ChevronLeftIcon fontSize="small" />
								</IconButton>
								<Typography className={classes.calendarMonth}>
									{MONTH_LABELS[calendarMonth.month]} {calendarMonth.year}
								</Typography>
								<IconButton size="small" onClick={() => setCalendarMonth(({ year, month }) => {
									const n = new Date(year, month + 1);
									return { year: n.getFullYear(), month: n.getMonth() };
								})}>
									<ChevronRightIcon fontSize="small" />
								</IconButton>
							</div>
							<div className={classes.calendarGrid}>
								{WEEKDAY_LABELS.map((d) => (
									<div key={d} className={classes.calendarWeekday}>{d}</div>
								))}
								{renderCalendar()}
							</div>

							{loadingSessions && (
								<div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
									<CircularProgress size={22} />
								</div>
							)}

							{/* CTA */}
							<Button
								variant="contained"
								color="primary"
								className={classes.ctaBtn}
								disabled={!selectedDate || loadingSessions}
								onClick={sessions.length > 0
									? () => sessionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
									: handleVerifyAvailability}
							>
								{!selectedDate
									? "Selecione uma data"
									: sessions.length > 0
									? "Ver opções disponíveis ↓"
									: "Verificar Disponibilidade"}
							</Button>

							{paxError && <Typography className={classes.paxError}>{paxError}</Typography>}
						</div>
					</Grid>
				</Grid>

				{/* Big session cards — shown after Verificar Disponibilidade */}
				{sessions.length > 0 && (
					<div ref={sessionsRef} className={classes.availabilitySection}>
						<Typography className={classes.availabilityTitle}>
							Temos disponibilidade para esta experiência, aproveite!
						</Typography>
						{sessions.map((session, idx) => {
							const SESSION_TYPE_LABEL = { 1: "Diurno", 2: "Vespertino", 3: "Noturno" };
							const sessionName = session.name || session.time || SESSION_TYPE_LABEL[session.type] || `Opção ${idx + 1}`;
							const langs = (session.guideLanguages || (session.language ? [session.language] : []))
								.map((l) => LANG_NAMES[l] || l).join(", ");
							const ptsPerPax = session.pointsPrice ?? null;
							const rateCategories = session.rates?.[0]?.categories || [];
							const totalPts = rateCategories.length > 0
								? (() => {
									const sum = rateCategories.reduce((acc, rc) => {
										const qty = paxCounts[rc.id] ?? 0;
										const pts = rc.pointsPrice ?? ptsPerPax ?? 0;
										if (detail?.isCategoryPaxGroup) return acc + (qty > 0 ? Math.round(pts) : 0);
										return acc + Math.round(pts * qty);
									}, 0);
									return sum > 0 ? sum : null;
								})()
								: (ptsPerPax != null ? Math.round(ptsPerPax * totalPax) : null);
							const sessionMinAge = getSessionMinAge(rateCategories, detail);
							const availability = sessionAvailability(session, totalPax, isGroup);
							const isSelected = selectedSession === session;
							return (
								<div
									key={idx}
									className={`${classes.bigSessionCard} ${isSelected ? classes.bigSessionCardSelected : ""}`}
								>
									<span className={classes.bigSessionBadge}>Opção {idx + 1}</span>

									{/* Left */}
									<div className={classes.bigSessionLeft}>
										<Typography className={classes.bigSessionTypeName}>{sessionName}</Typography>
										{langs && <Typography style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>{langs}</Typography>}
										<div className={classes.bigSessionDivider} />
										<div className={classes.bigSessionMetaRow}>
											<div>
												<p className={classes.bigSessionMetaLabel}>Data</p>
												<p className={classes.bigSessionMetaValue}>
													{selectedDate ? (() => {
														const [year, month, day] = selectedDate.split('-');
														return `${day}/${month}/${year}`;
													})() : ''}
												</p>
											</div>
											{(session.time || session.startTime) && (
												<div>
													<p className={classes.bigSessionMetaLabel}>Saída</p>
													<p className={classes.bigSessionMetaValue}>{session.time || session.startTime}</p>
												</div>
											)}
											{sessionMinAge > 0 && (
												<div>
													<p className={classes.bigSessionMetaLabel}>Idade mínima</p>
													<p className={classes.bigSessionMetaValue}>{sessionMinAge} anos</p>
												</div>
											)}
										</div>
									</div>

									{/* Right */}
									<div className={classes.bigSessionRight}>
										{totalPts != null && (
											<>
												<p className={classes.bigSessionPriceLabel}>Preço do serviço</p>
												<p className={classes.bigSessionPriceValue}>
													{totalPts.toLocaleString("pt-BR")}
													<span style={{ fontSize: 14, fontWeight: 600 }}> pts</span>
												</p>
											</>
										)}
										{paxCategories.map((cat) => {
											const qty = paxCounts[cat.id] ?? 0;
											if (qty === 0) return null;
											const rateCategory = rateCategories.find((c) => c.id === cat.id);
											const isFree = rateCategory?.price === 0;
											const catPts = rateCategory?.pointsPrice ?? ptsPerPax;
											const catAge = formatAgeRange(rateCategory?.age ?? cat.age);
											return (
												<p key={cat.id} className={classes.bigSessionPaxLine}>
													{detail?.isCategoryPaxGroup
														? `${cat.text} (${qty} pessoa${qty !== 1 ? "s" : ""})`
														: `${qty}x ${cat.text}`}
													{catAge ? ` (${catAge})` : ""}
													{!isFree && catPts != null ? ` ${Math.round(catPts)} pts` : " 0 pts"}
												</p>
											);
										})}
										{availability.message && (
											<p
												className={classes.bigSessionQuota}
												style={{ color: availability.bookable ? "#e67e22" : "#d32f2f" }}
											>
												{availability.message}
											</p>
										)}
										<Button
											variant="contained"
											color="primary"
											className={classes.bigSessionSelectBtn}
											disabled={!availability.bookable}
											onClick={() => handleReserve(session)}
										>
											{availability.bookable ? "Selecionar" : "Indisponível"}
										</Button>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</Container>
		</div>
		<RegisterModal
			open={isOpenRegisterModal}
			onClose={handleRegisterModal}
			travelVoucherUser={user?.travelVoucher}
		/>
		</>
	);
};

export default CivitatisActivityDetail;
